import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  useMediaQuery,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import moment from 'moment';
import { toast } from 'react-toastify';
import ApiService from '../../functions/apiService';
import CollapseComponent from '../../components/Schedule/CollapseComponent';
import SelectRebuild from '../../components/Inputs/SelectRebuild';
import { ExportCsvButton } from '../../components/Buttons/ExportCsvButton';
import { FilterPanel } from '../../components/Reports/FilterPanel';
import ReportsHeader from '../../components/Reports/ReportsHeader';
import DataNotFound from '../../components/Reports/DataNotFound';
import { useDateRangeFilter } from './hooks/useDateRangeFilter';
import './reports.css';

const defaultFormValues = {
  emp_code: [],
  certification: null,
};

const shiftColumns = [
  {
    field: 'shift_date',
    headerName: 'Shift Date',
    flex: 1,
    valueGetter: params =>
      params?.row?.shift_date
        ? moment(params.row.shift_date).format('MM/DD/YYYY')
        : ''
  },
  { field: 'shift_code', headerName: 'Shift Code', flex: 1 },
  { field: 'shift_name', headerName: 'Shift Name', flex: 1 },
  { field: 'position_name', headerName: 'Certificate/Position', flex: 1, minWidth: 140 },
  { field: 'start_time', headerName: 'Start Time', flex: 1 },
  { field: 'end_time', headerName: 'End Time', flex: 1 },
  { field: 'hours_worked', headerName: 'Hours', flex: 1 }
];

const getTotalHoursRow = (shifts = []) => {
  const total = shifts.reduce(
    (sum, shift) => sum + Number(shift.hours_worked || 0),
    0
  );

  return {
    id: 'total-row',
    shift_date: '',
    shift_code: '',
    shift_name: '',
    start_time: '',
    end_time: 'TOTAL HOURS',
    hours_worked: total
  };
};

const VolunteerReport = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [employeeList, setEmployeeList] = useState([]);
  const [loadingEmployeeList, setLoadingEmployeeList] = useState(false);
  const [certificateOptions, setCertificateOptions] = useState([]);
  const isTabletVerticalWidth = useMediaQuery('(max-width:1024px)');

  const { fromDate, toDate, render } = useDateRangeFilter(false, undefined, true);

  const { register, setValue, watch } = useForm({
    defaultValues: defaultFormValues,
  });
  const { emp_code: employees, certification } = watch();

  const getEmployeeSwapReport = (method) => {
    setLoading(true);
    const payload = {
      start_date: moment(fromDate).format('YYYY-MM-DD'),
      end_date: moment(toDate).format('YYYY-MM-DD'),
      emp_codes: employees?.length > 0 ? employees.map((emp) => emp.id) : '',
      Certificate_id: certification?.id || '',
      method
    };

    ApiService.post('/reports/volunteer-payroll-report', payload)
      .then((response) => {
        setLoading(false);

        if (!method) {
          setRows(Array.isArray(response.data) ? response.data : []);
          return;
        }

        if (method === 'csv') {
          const blob = new Blob(
            [response],
            { type: 'text/csv;charset=utf-8;' }
          );

          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');

          link.href = url;
          link.download = 'volunteer-report.csv';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          return;
        }

        if (method === 'pdf') {
          const pdfUrl = response?.pdf_url;

          if (pdfUrl) {
            window.open(pdfUrl, '_blank');
          } else {
            toast.error('PDF URL not found');
          }
          return;
        }
      })
      .catch(err => {
        setLoading(false);
        toast.error(
          err?.response?.data?.message || 'Failed to fetch data'
        );
      });
  };

  const loadCertificateDropdown = () => {
    ApiService.get('/get-certificates-dropdown')
      .then(({ data }) => {
        const certArr = (data?.data || []).map((cer) => ({
          id: cer.Certificate_id,
          name: cer.Name,
        }));
        setCertificateOptions(certArr);
      })
      .catch(() => {});
  };

  const loadEmployees = () => {
    setLoadingEmployeeList(true);
    ApiService.get('/employeeToSwap')
      .then(({ data }) => {
        const list = (data || []).map((emp) => ({
          id: emp.Emp_Code,
          name: `${emp.EA_Rank} ${emp.fullName}`,
        }));
        setEmployeeList(list);
        setLoadingEmployeeList(false);
      })
      .catch(() => {
        setLoadingEmployeeList(false);
      });
  };

  useEffect(() => {
    getEmployeeSwapReport();
    loadEmployees();
    loadCertificateDropdown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rawFilterConfig = [
    {
      key: 'emp_code',
      label: 'Employees',
      show: true,
      renderComponent: () => (
        <SelectRebuild
          options={employeeList}
          name="emp_code"
          label="Employees"
          multiple
          allOption={{ id: 'ALL', name: 'All' }}
          loading={loadingEmployeeList}
          register={register}
          setValue={setValue}
          watch={watch}
        />
      ),
    },
    {
      key: 'certification',
      label: 'Certification List',
      show: true,
      renderComponent: () => (
        <SelectRebuild
          options={certificateOptions}
          name="certification"
          label="Certification List"
          multiple={false}
          loading={false}
          register={register}
          setValue={setValue}
          watch={watch}
        />
      ),
    },
    {
      key: 'find_button',
      label: '',
      show: true,
      renderComponent: () => (
        <Grid
          item
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '100%',
            paddingTop: '23px',
          }}
        >
          <Button
            variant="contained"
            onClick={() => getEmployeeSwapReport()}
            sx={{ whiteSpace: 'nowrap', height: '32px' }}
          >
            Find
          </Button>
        </Grid>
      ),
    },
    {
      key: 'export',
      label: '',
      show: rows.length > 0,
      renderComponent: () => (
        <Grid
          item
          sx={{
            width: '100%',
            display: 'flex',
            gap: 2,
            justifyContent: isTabletVerticalWidth ? 'start' : 'end',
            alignItems: 'center',
            height: '100%',
            paddingTop: '23px',
          }}
        >
          <ExportCsvButton
            onClick={() => getEmployeeSwapReport('csv')}
            text={'Export CSV'}
          />
          <ExportCsvButton
            onClick={() => getEmployeeSwapReport('pdf')}
            text={'Export PDF'}
          />
        </Grid>
      ),
    },
  ];

  const filterConfig = rawFilterConfig.filter((f) => f.show !== false);

  return (
    <Paper
      elevation={0}
      className="p-4"
      sx={{ marginTop: '1rem', border: '1px solid #EAECF0', borderRadius: '12px' }}
    >
      <ReportsHeader title="Volunteer Report" />

      <Box className="mt-4" sx={{ width: '100%' }}>
        <Grid container sx={{ width: '100%', mb: '24px' }}>
          <FilterPanel
            filters={filterConfig}
            columns={isTabletVerticalWidth ? 1 : 4}
          />
        </Grid>

        <Grid container sx={{ width: '100%', mb: '24px' }}>
          <Grid
            item
            sx={{
              display: 'flex',
              gap: 2,
              width: '100%',
              alignItems: 'center',
            }}
          >
            <Grid
              item
              sx={{
                display: 'flex',
                flexDirection: isTabletVerticalWidth ? 'column' : 'row',
                gap: 2,
                width: '100%',
                mt: 'auto',
              }}
            >
              {render}
            </Grid>
          </Grid>
        </Grid>

        {loading && (
          <Box
            sx={{ minHeight: '100px' }}
            className="w-100 h-100 d-flex justify-content-center align-items-center"
          >
            <CircularProgress />
          </Box>
        )}

        {!loading && rows.length === 0 && <DataNotFound />}

        {!loading && rows.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {rows.map((emp, index) => {
              const shiftRows = emp.shifts.map((shift, i) => ({
                id: i,
                ...shift
              }));

              return (
                <CollapseComponent
                  key={emp.emp_code || index}
                  name={`${emp.employee_name} (${emp.emp_code})`}
                  rows={[...shiftRows, getTotalHoursRow(emp.shifts)]}
                  columns={shiftColumns}
                  checkboxSelection={false}
                  open={true}
                  height="auto"
                  showHeader={true}
                />
              );
            })}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default VolunteerReport;
